/**
 * Module dependencies.
 */

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleAlert, CircleCheck, X } from 'lucide-react';
import { cn } from 'app/lib/utils';

/**
 * Types.
 */

type ToastType = 'error' | 'success';

type ToastMessage = {
  message?: React.ReactNode;
  description?: React.ReactNode;
};

type Toast = ToastMessage & {
  id: number;
  type: ToastType;
  leaving?: boolean;
};

type ToastApi = {
  error: (message: ToastMessage) => void;
  success: (message: ToastMessage) => void;
};

/**
 * `ToastContext` context.
 */

const ToastContext = createContext<ToastApi>({
  error: () => undefined,
  success: () => undefined
});

/**
 * Export `useToast` hook.
 */

export function useToast(): ToastApi {
  return useContext(ToastContext);
}

/**
 * Auto-dismiss delay (ms).
 */

const DISMISS_MS = 5000;

/**
 * Exit-animation duration (ms) — the toast stays mounted this long after being
 * dismissed so its leave animation can play, then it's unmounted.
 */

const EXIT_MS = 220;

/**
 * Export `ToastProvider` component.
 *
 * Lightweight replacement for antd's `notification` — same error/success API
 * (message + description), rendered bottom-left and auto-dismissed.
 */

export function ToastProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  // Dismiss with a leave animation: flag the toast as `leaving` (plays the exit
  // animation), then unmount it once the animation has finished.
  const remove = useCallback((id: number) => {
    setToasts(current => current.map(toast => (toast.id === id ? { ...toast, leaving: true } : toast)));
    setTimeout(() => setToasts(current => current.filter(toast => toast.id !== id)), EXIT_MS);
  }, []);

  const push = useCallback(
    (type: ToastType, message: ToastMessage) => {
      const id = (idRef.current += 1);
      const toast: Toast = { id, type, ...message };

      // De-duplicate: a repeated toast (same type + message) replaces the
      // previous one *in place* — the fresh id re-mounts that slot so its enter
      // animation replays (reads as a new output), with no layout jump.
      setToasts(current => {
        const index = current.findIndex(
          existing => existing.type === type && existing.message === message.message && !existing.leaving
        );

        if (index === -1) {
          return [...current, toast];
        }

        const next = [...current];
        next[index] = toast;

        return next;
      });

      setTimeout(() => remove(id), DISMISS_MS);
    },
    [remove]
  );

  const api = useMemo<ToastApi>(
    () => ({
      error: message => push('error', message),
      success: message => push('success', message)
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      {createPortal(
        <div className={'pointer-events-none fixed bottom-4 left-4 z-[100] flex w-full max-w-sm flex-col gap-2'}>
          {toasts.map(toast => (
            <div
              className={cn(
                'pointer-events-auto flex items-start gap-3 rounded-lg border bg-card p-3.5 shadow-lg duration-200',
                toast.type === 'error' ? 'border-destructive/30' : 'border-border',
                toast.leaving ? 'animate-out fade-out zoom-out-95' : 'animate-in fade-in slide-in-from-bottom-2'
              )}
              key={toast.id}
              role={'status'}
            >
              {toast.type === 'error' ? (
                <CircleAlert className={'h-5 w-5 shrink-0 text-destructive'} />
              ) : (
                <CircleCheck className={'h-5 w-5 shrink-0 text-primary'} />
              )}

              <div className={'min-w-0 flex-1'}>
                {!!toast.message && <div className={'text-sm font-semibold text-foreground'}>{toast.message}</div>}

                {!!toast.description && (
                  <div className={'mt-0.5 break-words text-sm text-muted-foreground'}>{toast.description}</div>
                )}
              </div>

              <button
                aria-label={'Close'}
                className={
                  'shrink-0 rounded-md p-0.5 text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground'
                }
                onClick={() => remove(toast.id)}
                type={'button'}
              >
                <X className={'h-4 w-4'} />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}
